import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { createDistributorUser } from '../services/DistributorCRUD';
import { DistributorModel } from '../models/DistributorModel';
import clsx from 'clsx';

type Props = {
  visible: boolean;
  onClose: () => void;
  distributors: DistributorModel[];
};

const INITIAL_DISTRIBUTOR_USER = {
  name: '',
  email: '',
  password: '',
  repeatPassword: '',
  distributorId: null,
};

export default function ModalDistributorUserForm({
  distributors,
  visible,
  onClose,
}: Props) {
  const intl = useIntl();
  const [distributorUserRequest, setDistributorUserRequest] = useState(
    INITIAL_DISTRIBUTOR_USER
  );
  const distributorOptions = distributors
    .sort((a, b) => b.id - a.id)
    .map((distributor) => ({
      value: distributor.id.toString(),
      label: `${distributor.id}. ${distributor.name} - ${distributor?.vat_number || '-'}`,
    }));

  async function onSubmit() {
    try {
      await createDistributorUser(distributorUserRequest);
      toast.success(
        intl.formatMessage({ id: 'distributors.createDistributorUserSuccess' })
      );
      onClose();
    } catch (error) {
      console.warn(error);
      toast.error(
        intl.formatMessage({ id: 'distributors.createDistributorUserError' })
      );
    }
  }

  useEffect(() => {
    if (visible) {
      setDistributorUserRequest(INITIAL_DISTRIBUTOR_USER);
    }
  }, [visible]);

  return (
    <Modal show={visible} onClose={onClose}>
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">
            {intl.formatMessage({
              id: 'distributors.createDistributorAccount',
            })}
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
          ></button>
        </div>
        <div className="modal-body">
          <form>
            <div className="row mb-3">
              <div className="col-12">
                <label htmlFor="distributorId" className="form-label">
                  {intl.formatMessage({ id: 'distributors.distributor' })} *
                </label>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={distributorOptions}
                  value={
                    distributorOptions.find(
                      (option) =>
                        parseInt(option.value, 10) ===
                        distributorUserRequest?.distributorId
                    ) || null
                  }
                  onChange={(distributor) =>
                    setDistributorUserRequest({
                      ...distributorUserRequest,
                      distributorId: parseInt(distributor?.value, 10),
                    })
                  }
                  placeholder={intl.formatMessage({
                    id: 'distributors.searchDistributor',
                  })}
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-6">
                <label htmlFor="email" className="form-label">
                  {intl.formatMessage({ id: 'distributors.email' })} *
                </label>
                <input
                  type="email"
                  value={distributorUserRequest?.email}
                  onChange={(e) =>
                    setDistributorUserRequest({
                      ...distributorUserRequest,
                      email: e.target.value,
                    })
                  }
                  className="form-control"
                  id="email"
                  placeholder={intl.formatMessage({
                    id: 'distributors.emailPlaceholder',
                  })}
                />
              </div>
              <div className="col-6">
                <label htmlFor="name" className="form-label">
                  {intl.formatMessage({ id: 'distributors.name' })} *
                </label>
                <input
                  type="text"
                  value={distributorUserRequest?.name}
                  onChange={(e) =>
                    setDistributorUserRequest({
                      ...distributorUserRequest,
                      name: e.target.value,
                    })
                  }
                  className="form-control"
                  id="name"
                  placeholder={intl.formatMessage({
                    id: 'distributors.namePlaceholder',
                  })}
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-6">
                <label htmlFor="password" className="form-label">
                  {intl.formatMessage({ id: 'distributors.password' })} *
                </label>
                <input
                  type="password"
                  value={distributorUserRequest?.password}
                  onChange={(e) =>
                    setDistributorUserRequest({
                      ...distributorUserRequest,
                      password: e.target.value,
                    })
                  }
                  className="form-control"
                  id="password"
                  placeholder={intl.formatMessage({
                    id: 'distributors.passwordPlaceholder',
                  })}
                />
              </div>
              <div className="col-6">
                <label htmlFor="repeatPassword" className="form-label">
                  {intl.formatMessage({ id: 'distributors.repeatPassword' })} *
                </label>
                <input
                  type="password"
                  value={distributorUserRequest?.repeatPassword}
                  onChange={(e) =>
                    setDistributorUserRequest({
                      ...distributorUserRequest,
                      repeatPassword: e.target.value,
                    })
                  }
                  className={clsx('form-control', {
                    'is-invalid':
                      distributorUserRequest?.password !==
                      distributorUserRequest?.repeatPassword,
                  })}
                  id="repeatPassword"
                  placeholder={intl.formatMessage({
                    id: 'distributors.repeatPasswordPlaceholder',
                  })}
                />
                <span className="invalid-feedback">
                  {intl.formatMessage({
                    id: 'distributors.passwordsDoNotMatch',
                  })}
                </span>
              </div>
            </div>
          </form>

          <div className="d-flex gap-4 pt-8 justify-content-end">
            <button
              type="button"
              className="btn btn-tertiary"
              data-bs-dismiss="modal"
              onClick={onClose}
            >
              {intl.formatMessage({ id: 'common.close' })}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onSubmit}
              disabled={
                !distributorUserRequest?.name ||
                !distributorUserRequest?.email ||
                !distributorUserRequest?.password ||
                !distributorUserRequest?.repeatPassword ||
                distributorUserRequest?.password !==
                  distributorUserRequest?.repeatPassword ||
                !distributorUserRequest?.distributorId
              }
            >
              <span className="fa fa-save me-3"></span>
              {intl.formatMessage({ id: 'common.save' })}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
